"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Tabs, TabPanel } from "react-aria-components";
import { AnimatePresence } from "motion/react";
import * as m from "motion/react-m";
import clsx from "clsx";
import { useTranslations } from "next-intl";

import { captureEvent } from "@/app/_analytics/analytics";
import { EventName } from "@mw/shared/analyticsEventNames";
import IconPointer from "@ui/central-icons/icons/IconPointer";
import { NativeAudioProvider, useNativeAudio } from "@/app/_providers/native-audio";
import { appUrls } from "@/constants";
import { TabList } from "@ui/components/core/TabList";
import { toDiscImageConfig } from "@ui/components/core/Disc/Disc";
import { DiscPlayer } from "@ui/components/core/Disc/DiscPlayer";
import { usePlaybackNudge } from "../hooks/usePlaybackNudge";
import { NudgeFooter } from "../NudgeFooter";
import { getAudioUrl } from "@utilities/payload";
import type { Media } from "@mw/shared/payload-types";

import { SideBySideOmniboxLayout } from "@ui/components/core/SideBySideOmnibox";
import IsolateYourVoiceView from "./IsolateYourVoiceView";
import { useVoiceIsolate } from "./hooks/useVoiceIsolate";
import { OmniboxLayoutWrapper } from "../OmniboxLayoutWrapper";

export interface VoiceIsolatorV2Sample {
  id: string;
  title: string;
  description: string;
  original: {
    label: string;
    image: Media;
    audio: Media;
  };
  isolated: {
    label: string;
    image?: Media;
    audio: Media;
  };
}

export interface VoiceIsolatorV2Props {
  samples: VoiceIsolatorV2Sample[];
  defaultSelectedSampleId?: string;
  defaultTab?: "samples" | "isolate" | null;
}

const SYNC_OFFSET_SECONDS = 3;

export default function VoiceIsolatorV2({ samples, defaultSelectedSampleId, defaultTab }: VoiceIsolatorV2Props) {
  return (
    <NativeAudioProvider>
      <VoiceIsolatorV2Inner
        samples={samples}
        defaultSelectedSampleId={defaultSelectedSampleId}
        defaultTab={defaultTab}
      />
    </NativeAudioProvider>
  );
}

function VoiceIsolatorV2Inner({ samples, defaultSelectedSampleId, defaultTab }: VoiceIsolatorV2Props) {
  const t = useTranslations("ClientKeys.VoiceIsolatorV2");
  const { togglePlay, stop, playingId } = useNativeAudio();
  const lastPlayedDiscRef = useRef<"left" | "right" | null>(null);
  const [selectedSampleId, setSelectedSampleId] = useState<string | undefined>(
    defaultSelectedSampleId ?? samples[0]?.id,
  );
  const [selectedOuterTab, setSelectedOuterTab] = useState<"samples" | "isolate">(defaultTab ?? "samples");
  const currentTimeRef = useRef<number>(0);
  const voiceIsolateData = useVoiceIsolate();
  const { showLimitReachedOverlay, setShowLimitReachedOverlay } = voiceIsolateData;

  const nudge = usePlaybackNudge(lastPlayedDiscRef, {
    impressionEventName: EventName.mw_voice_isolator_nudge_shown,
    suppressionKey: null,
  });
  const shouldShowNudgeFooter = nudge.shouldShow(selectedOuterTab, showLimitReachedOverlay);

  useEffect(() => {
    if (shouldShowNudgeFooter) nudge.trackImpression();
  }, [shouldShowNudgeFooter, nudge]);

  const ontimeupdate = useCallback(
    (currentTime: number) => {
      currentTimeRef.current = currentTime;
      nudge.onTimeUpdate(currentTime);
    },
    [nudge],
  );

  const sampleTabs = useMemo(() => {
    return samples.map(sample => ({
      id: sample.id,
      title: sample.title,
    }));
  }, [samples]);

  const sampleId2Sample = useMemo(() => {
    return samples.reduce(
      (acc, sample) => {
        acc[sample.id] = sample;
        return acc;
      },
      {} as Record<string, VoiceIsolatorV2Sample>,
    );
  }, [samples]);

  const currentSample = selectedSampleId ? sampleId2Sample[selectedSampleId] : null;

  const handleDiscClick = (
    disc: "left" | "right",
    sample: VoiceIsolatorV2Sample,
    trigger: "manual_click" | "auto_play_on_sample_switch" = "manual_click",
  ) => {
    const audioUrl = disc === "left" ? getAudioUrl(sample.original.audio) : getAudioUrl(sample.isolated.audio);
    if (!audioUrl) return;

    const startTime = Math.max(0, currentTimeRef.current - SYNC_OFFSET_SECONDS);

    if (disc === "right") {
      nudge.resetTrackTime(startTime);
    }

    const voiceIndex = samples.findIndex(s => s.id === sample.id);
    captureEvent(EventName.mw_voice_isolator_playback_toggled, {
      voice_index: voiceIndex,
      voice_id: sample.title,
      audio_type: disc === "left" ? "original" : "isolated",
      trigger,
      component: "voice_isolator_v2",
    });

    togglePlay({
      id: audioUrl,
      url: audioUrl,
      loop: true,
      startTime,
      onTimeUpdate: ontimeupdate,
    });
    lastPlayedDiscRef.current = disc;
  };

  const handleSampleChange = (newSampleId: string) => {
    const lastDisc = lastPlayedDiscRef.current;
    const wasPlaying = !!playingId;
    const newSample = sampleId2Sample[newSampleId];

    const voiceIndex = samples.findIndex(s => s.id === newSampleId);
    captureEvent(EventName.mw_voice_isolator_audio_type_selected, {
      voice_index: voiceIndex,
      voice_id: newSample.title,
      was_playing: wasPlaying,
    });

    // Update current sample reference
    setSelectedSampleId(newSampleId);
    currentTimeRef.current = 0;

    if (!wasPlaying) return;

    stop();

    if (!lastDisc) return;

    // Small delay to ensure stop completes
    setTimeout(() => {
      handleDiscClick(lastDisc, newSample, "auto_play_on_sample_switch");
    }, 50);
  };

  return (
    <OmniboxLayoutWrapper
      showLimitReachedOverlay={showLimitReachedOverlay}
      onCloseLimitReachedOverlay={() => setShowLimitReachedOverlay(false)}
      showFooter={shouldShowNudgeFooter}
      footer={
        <NudgeFooter
          gradientTo='tw-to-cream'
          hidden={!shouldShowNudgeFooter}
          link={{
            href: appUrls.voiceIsolator,
            eventName: EventName.mw_voice_isolator_nudge_clicked,
          }}
        >
          <IconPointer className='tw-size-3.5 tw-shrink-0 tw-rotate-90 tw-mr-1.5 tw-hidden sm:tw-block' />
          <span className='sm:tw-hidden'>{t("nudge_cta_short")}</span>
          <span className='tw-hidden sm:tw-inline'>{t("nudge_cta")}</span>
        </NudgeFooter>
      }
    >
      <Tabs
        className={clsx(
          "tw-size-full tw-flex tw-flex-col tw-justify-between tw-transition-[padding-bottom] tw-duration-300",
          shouldShowNudgeFooter && "tw-pb-11 md:tw-pb-8",
        )}
        selectedKey={selectedOuterTab}
        onSelectionChange={key => {
          const newTab = key as "samples" | "isolate";
          captureEvent(EventName.mw_voice_isolator_audio_type_selected, {
            previous_tab: selectedOuterTab,
            new_tab: newTab,
          });
          stop();
          setSelectedOuterTab(newTab);
        }}
      >
        {/* Header with title and toggle tabs */}
        <div className='tw-flex tw-items-center md:tw-items-start md:tw-justify-between tw-justify-center tw-gap-4'>
          <div className='tw-flex-1 tw-min-w-0 tw-hidden md:tw-block'>
            <VoiceIsolatorV2Header sample={currentSample ?? undefined} selectedOuterTab={selectedOuterTab} />
          </div>
          <div className='md:tw-flex-1 tw-min-w-0 md:tw-flex md:tw-justify-end'>
            <TabList
              size='sm-responsive'
              surface='cream'
              className='md:tw-h-11 lg:tw-h-12'
              items={[
                { id: "samples", title: t("tab_examples") },
                { id: "isolate", title: t("tab_isolate_your_own") },
              ]}
            />
          </div>
        </div>

        {/* Content area */}
        <div className='tw-flex tw-flex-1 tw-flex-col tw-min-h-0 tw-relative'>
          <AnimatePresence initial={false}>
            {selectedOuterTab === "samples" && (
              <m.div
                key='samples'
                className='tw-absolute tw-inset-0 tw-flex tw-flex-col'
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
              >
                <TabPanel id='samples' className='tw-flex tw-flex-1 tw-flex-col tw-min-h-0' shouldForceMount>
                  <Tabs
                    className='tw-size-full tw-flex tw-flex-col tw-min-h-0'
                    selectedKey={selectedSampleId}
                    onSelectionChange={key => handleSampleChange(key as string)}
                  >
                    <div className='tw-flex tw-flex-1 tw-min-h-0 tw-overflow-hidden'>
                      {currentSample && (
                        <TabPanel id={selectedSampleId} className='tw-flex tw-flex-1 tw-min-h-0' shouldForceMount>
                          <SideBySideOmniboxLayout
                            leftLabel={currentSample.original.label}
                            rightLabel={currentSample.isolated.label}
                            leftContent={
                              <DiscPlayer
                                image={toDiscImageConfig(currentSample.original.image)}
                                isPlaying={playingId === getAudioUrl(currentSample.original.audio)}
                                onClick={() => handleDiscClick("left", currentSample)}
                              />
                            }
                            rightContent={
                              <DiscPlayer
                                image={toDiscImageConfig(currentSample.isolated.image)}
                                isPlaying={playingId === getAudioUrl(currentSample.isolated.audio)}
                                onClick={() => handleDiscClick("right", currentSample)}
                              />
                            }
                          />
                        </TabPanel>
                      )}
                    </div>
                    <div className='tw-flex tw-flex-col tw-justify-end'>
                      <TabList
                        items={sampleTabs}
                        centered
                        size='sm-responsive'
                        surface='cream'
                        showRing={false}
                        className='md:tw-h-11 lg:tw-h-12'
                      />
                    </div>
                  </Tabs>
                </TabPanel>
              </m.div>
            )}
            {selectedOuterTab === "isolate" && (
              <m.div
                key='isolate'
                className='tw-absolute tw-inset-0 tw-flex tw-flex-col'
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
              >
                <TabPanel id='isolate' className='tw-flex tw-size-full tw-flex-col' shouldForceMount>
                  <IsolateYourVoiceView voiceIsolateData={voiceIsolateData} />
                </TabPanel>
              </m.div>
            )}
          </AnimatePresence>
        </div>
      </Tabs>
    </OmniboxLayoutWrapper>
  );
}

function VoiceIsolatorV2Header({
  sample,
  selectedOuterTab,
}: {
  sample?: VoiceIsolatorV2Sample;
  selectedOuterTab: "samples" | "isolate";
}) {
  const t = useTranslations("ClientKeys.VoiceIsolatorV2");
  const showSample = sample && selectedOuterTab !== "isolate";

  return (
    <>
      <h2 className='tw-text-xs sm:tw-text-base lg:tw-text-lg lg:tw-pb-1 tw-type-xs sm:tw-type-base lg:tw-type-lg'>
        {showSample ? sample.title : t("default_header_title")}
      </h2>
      <p className='tw-text-2xs tw-leading-snug lg:tw-text-sm tw-text-black tw-opacity-60 tw-line-clamp-2 tw-min-h-[2lh]'>
        {showSample ? sample.description : t("default_header_description")}
      </p>
    </>
  );
}
